/**
 * Shared utility functions for progress tracking across different agents.
 * Provides tool call extraction, conversion, and segmentation by exchange.
 */

import {LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ChatSessionQuery} from 'llm/chat-library/__generated/ChatSessionQuery';
import {ChatEventNode, LLMToolCallBlock, LLMToolResultBlock} from 'llm/chat-library/messages/MessageTypes';
import {getToolDisplayName, shouldDisplayTool} from 'llm/chat-library/tool-details/types';

import {applyOptimisticToolResults, OptimisticToolResultsById} from './optimisticToolResults';
import {
  ALL_INTERRUPT_SKIPPED_TOOL_RESULT_TEXTS,
  ProgressData,
  ProgressToolCall,
  ToolCallStatus,
} from './ProgressTypes';

export interface CurrentExchangeProgressRealtimeState {
  queuedToolCallIds: ReadonlySet<string>;
  runningToolCallIds: ReadonlySet<string>;
  optimisticToolResultsById: OptimisticToolResultsById;
  hasReceivedSnapshot: boolean;
  hasExplicitQueuedToolCallIds: boolean;
  isThreadActive: boolean;
}

function resolveExecutionStatus(
  toolCallId: string,
  realtimeState: CurrentExchangeProgressRealtimeState
): ToolCallStatus {
  if (realtimeState.runningToolCallIds.has(toolCallId)) {
    return ToolCallStatus.RUNNING;
  }
  if (!realtimeState.hasReceivedSnapshot) {
    return ToolCallStatus.UNKNOWN;
  }
  if (realtimeState.hasExplicitQueuedToolCallIds) {
    return realtimeState.queuedToolCallIds.has(toolCallId) ? ToolCallStatus.QUEUED : ToolCallStatus.UNKNOWN;
  }
  return ToolCallStatus.QUEUED;
}

function applyToolProgressExecutionState(
  progressData: ProgressData,
  realtimeState: CurrentExchangeProgressRealtimeState
): ProgressData {
  if (!realtimeState.isThreadActive) {
    return progressData;
  }

  let changed = false;
  const toolCalls = progressData.toolCalls.map((toolCall): ProgressToolCall => {
    if (toolCall.status === ToolCallStatus.COMPLETED) {
      return toolCall;
    }

    const nextStatus = resolveExecutionStatus(toolCall.toolId, realtimeState);
    if (toolCall.status === nextStatus) {
      return toolCall;
    }

    changed = true;
    return {
      ...toolCall,
      status: nextStatus,
      displayName: getToolDisplayName(toolCall.tool, nextStatus, toolCall.args),
    };
  });

  return changed ? {toolCalls} : progressData;
}

function applyRealtimeProgressState(
  progressData: ProgressData | null,
  realtimeState: CurrentExchangeProgressRealtimeState | undefined
): ProgressData | null {
  if (progressData == null || realtimeState == null) {
    return progressData;
  }

  let merged = applyToolProgressExecutionState(progressData, realtimeState);
  if (realtimeState.isThreadActive) {
    merged = applyOptimisticToolResults(merged, realtimeState.optimisticToolResultsById) ?? merged;
  }
  return merged;
}

export type ChatSessionNode = ExtractByTypename<ChatSessionQuery['chatSession'], 'ChatSessionNode'>;

/**
 * Maps assistant message IDs to the tool calls that occurred before that message.
 */
export interface ToolCallsByAssistantMessage {
  [assistantMessageId: string]: ProgressData;
}

/**
 * Maps completed exchange boundary event IDs to the tool calls that occurred before that boundary.
 */
export interface ToolCallsByCompletedProgressBoundary {
  [boundaryEventId: string]: ProgressData;
}

/**
 * Extract text content from a tool result block.
 */
export function extractResultText(toolResult: LLMToolResultBlock): string {
  return toolResult.value.content
    .filter((content) => content.type === 'text')
    .map((content) => {
      const textBlock = content;
      if (textBlock.value.text) {
        return textBlock.value.text;
      }
      return '';
    })
    .join(' ')
    .trim();
}

function isInterruptSkippedToolResult(resultText: string | null | undefined): boolean {
  return resultText != null && ALL_INTERRUPT_SKIPPED_TOOL_RESULT_TEXTS.includes(resultText.trim());
}

export function isToolCallSkipped(toolCall: Pick<ProgressToolCall, 'status' | 'responseText'>): boolean {
  return toolCall.status === ToolCallStatus.CANCELLED || isInterruptSkippedToolResult(toolCall.responseText);
}

export function shouldPreferToolResultBlock(
  existingToolResult: LLMToolResultBlock,
  nextToolResult: LLMToolResultBlock
): boolean {
  const existingResultIsSkipped = isInterruptSkippedToolResult(extractResultText(existingToolResult));
  const nextResultIsSkipped = isInterruptSkippedToolResult(extractResultText(nextToolResult));
  return existingResultIsSkipped || !nextResultIsSkipped;
}

/**
 * Create a ProgressToolCall from a tool call block and optional result.
 */
export function createProgressToolCall(
  toolCallBlock: LLMToolCallBlock,
  toolResultBlock: LLMToolResultBlock | null,
  createdAt: Date
): ProgressToolCall {
  const toolName = toolCallBlock.value.name;
  const resultText = toolResultBlock ? extractResultText(toolResultBlock) : undefined;
  const status =
    toolResultBlock == null
      ? ToolCallStatus.UNKNOWN
      : isInterruptSkippedToolResult(resultText)
        ? ToolCallStatus.CANCELLED
        : ToolCallStatus.COMPLETED;

  const args = toolCallBlock.value.args;
  return {
    tool: toolName,
    toolId: toolCallBlock.value.id,
    createdAt,
    status,
    displayName: getToolDisplayName(toolName, status, args),
    args,
    responseText: resultText,
  };
}

/**
 * Extract progress tool calls from a chat session's all threads.
 * Pairs tool calls with their corresponding results by matching IDs.
 * Tool results have the same ID as their corresponding tool call.
 */
export function getProgressToolCalls(chatSession: ChatSessionNode): Array<ProgressToolCall> {
  return getToolCalls(chatSession, shouldDisplayTool);
}

/**
 * Extract every tool call from a chat session's all threads.
 * Used for citation lookup because backend citations can point to tools that
 * are valid sources but intentionally hidden from the progress timeline.
 */
export function getAllToolCalls(chatSession: ChatSessionNode): Array<ProgressToolCall> {
  return getToolCalls(chatSession, () => true);
}

function getToolCalls(
  chatSession: ChatSessionNode,
  shouldIncludeTool: (toolName: string) => boolean
): Array<ProgressToolCall> {
  const toolMap = new Map<string, [LLMToolCallBlock, LLMToolResultBlock | null, Date]>();

  chatSession.allThreads.forEach((thread) => {
    thread.events.forEach((event) => {
      if (event.__typename === 'LLMMessageChatEventNode') {
        const createdAt = new Date(event.createdAt);

        event.messageBlocks?.forEach(({content: block}) => {
          if (block.type === 'tool_call') {
            toolMap.set(block.value.id, [block, null, createdAt]);
          } else if (block.type === 'tool_result') {
            const toolId = block.value.id;
            const existing = toolMap.get(toolId);
            if (existing) {
              const [toolCall, existingToolResult, date] = existing;
              if (existingToolResult != null && !shouldPreferToolResultBlock(existingToolResult, block)) {
                return;
              }
              toolMap.set(toolId, [toolCall, block, date]);
            }
          }
        });
      }
    });
  });

  return Array.from(toolMap.values())
    .map(([toolCall, toolResult, createdAt]) => {
      if (!shouldIncludeTool(toolCall.value.name)) {
        return null;
      }
      return createProgressToolCall(toolCall, toolResult, createdAt);
    })
    .filter((toolCall): toolCall is ProgressToolCall => toolCall != null)
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

/**
 * Get progress data for the current in-progress exchange only.
 * Returns tool calls that occurred after the last assistant message.
 */
export function getCurrentExchangeProgressData(
  chatSessionQuery: ChatSessionQuery,
  displayEvents: Array<ChatEventNode>,
  realtimeState?: CurrentExchangeProgressRealtimeState
): ProgressData | null {
  if (chatSessionQuery.chatSession.__typename === 'InaccessibleInfoNode') {
    return null;
  }

  const allToolCalls = getProgressToolCalls(chatSessionQuery.chatSession);
  if (allToolCalls.length === 0) {
    return null;
  }

  let lastCompletedExchangeBoundaryTime: Date | null = null;
  for (let i = displayEvents.length - 1; i >= 0; i--) {
    const event = displayEvents[i];
    if (
      (event.__typename === 'DisplayMessageChatEventNode' && event.role === LLMRole.ASSISTANT) ||
      event.__typename === 'InterruptChatEventNode'
    ) {
      lastCompletedExchangeBoundaryTime = new Date(event.createdAt);
      break;
    }
  }

  const currentExchangeToolCalls = allToolCalls.filter(
    (toolCall) =>
      lastCompletedExchangeBoundaryTime == null || toolCall.createdAt > lastCompletedExchangeBoundaryTime
  );

  if (currentExchangeToolCalls.length === 0) {
    return null;
  }

  return applyRealtimeProgressState(
    {
      toolCalls: currentExchangeToolCalls,
    },
    realtimeState
  );
}

/**
 * Segment tool calls by the assistant message they precede.
 * Returns a map from assistant message ID to the tool calls that happened
 * between the previous user message and that assistant message.
 */
export function getToolCallsByAssistantMessage(
  chatSessionQuery: ChatSessionQuery,
  displayEvents: Array<ChatEventNode>
): ToolCallsByAssistantMessage {
  return getToolCallsByCompletedProgressBoundary(chatSessionQuery, displayEvents);
}

/**
 * Segment tool calls by completed progress boundary.
 * Assistant display messages and interrupt events both end an exchange; the next user message
 * starts a fresh exchange.
 */
export function getToolCallsByCompletedProgressBoundary(
  chatSessionQuery: ChatSessionQuery,
  displayEvents: Array<ChatEventNode>
): ToolCallsByCompletedProgressBoundary {
  if (chatSessionQuery.chatSession.__typename === 'InaccessibleInfoNode') {
    return {};
  }

  const allToolCalls = getProgressToolCalls(chatSessionQuery.chatSession);
  if (allToolCalls.length === 0) {
    return {};
  }

  const result: ToolCallsByCompletedProgressBoundary = {};
  let toolCallIndex = 0;
  let inExchange = false;

  for (const event of displayEvents) {
    if (event.__typename === 'DisplayMessageChatEventNode' && event.role === LLMRole.USER) {
      inExchange = true;
      continue;
    }

    if (!inExchange) {
      continue;
    }

    const isAssistantMessageBoundary =
      event.__typename === 'DisplayMessageChatEventNode' && event.role === LLMRole.ASSISTANT;
    const isInterruptBoundary = event.__typename === 'InterruptChatEventNode';
    if (!isAssistantMessageBoundary && !isInterruptBoundary) {
      continue;
    }

    const boundaryCreatedAt = new Date(event.createdAt);
    const toolCallsForBoundary: Array<ProgressToolCall> = [];
    while (
      toolCallIndex < allToolCalls.length &&
      allToolCalls[toolCallIndex].createdAt <= boundaryCreatedAt
    ) {
      toolCallsForBoundary.push(allToolCalls[toolCallIndex]);
      toolCallIndex++;
    }

    if (toolCallsForBoundary.length > 0) {
      result[event.id] = {
        toolCalls: toolCallsForBoundary,
      };
    }
  }

  return result;
}
